// Front-end feature flags for the merged app (Voye send-products landing in the
// LakayCart portal). These gate NEW, additive surface so it stays invisible in
// production until deliberately switched on — the zero-downtime discipline of the
// merge: nothing that touches a live customer flow ships enabled by default.
//
// Read from Vite env at build time. A flag is ON only when its var is exactly
// the string 'true'; anything else (unset, 'false', '0') is OFF. Set per
// environment in the deploy config — never here.

/**
 * Airtime top-up in the customer portal. The backend endpoints are already live
 * (airtime_enabled=true server-side); this flag controls only whether the WEB
 * portal exposes the UI + route + nav item. OFF in production until the airtime
 * customer surface is signed off and rolled out staging-first.
 */
export function airtimeEnabled(): boolean {
  return import.meta.env.VITE_AIRTIME_ENABLED === 'true';
}

/**
 * Money transfer via Ria, in the customer portal.
 *
 * OFF, and it must stay off until Ria is actually signed. The server gates the
 * same capability on VOYE_MONEY_ENABLED and does not even REGISTER the /ria
 * routes when it is false, so turning this on alone would produce a screen
 * whose every call 404s. Both flags flip together.
 */
export function moneyEnabled(): boolean {
  return import.meta.env.VITE_MONEY_ENABLED === 'true';
}

/**
 * URL of the Voye mobile app (`voye-app`), for the "View on Phone" hand-off in
 * the portal. Set `VITE_MOBILE_APP_URL` per environment; when it is unset we
 * derive a dev default from the CURRENT hostname on the mobile dev port, so a
 * tester browsing the portal over the LAN gets a LAN-reachable phone URL rather
 * than a `localhost` one their phone cannot resolve.
 */
/** Where the mobile app lives in production. */
export const MOBILE_APP_PRODUCTION_URL = 'https://m.lakaycart.com';

/**
 * A private address — LAN, loopback, or plain HTTP. Unreachable to anyone off
 * the machine that built the bundle.
 */
function isPrivateUrl(u: string): boolean {
  return /^https?:\/\/(localhost|127\.|0\.0\.0\.0|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/i.test(u)
    || /^http:\/\//i.test(u);
}

export function mobileAppUrl(): string {
  const configured = import.meta.env.VITE_MOBILE_APP_URL;

  if (typeof configured === 'string' && configured.trim() !== '') {
    const url = configured.trim().replace(/\/+$/, '');

    // A production build must NEVER ship a LAN address. `.env.local` sets this
    // to the dev machine's IP for LAN testing, and Vite's loadEnv reads that
    // file OVER shell variables — so a production build silently baked in
    // http://192.168.68.56:5173/voye, and every customer who tapped "View on
    // Phone" was shown a QR code pointing at a developer's laptop. Guard the
    // value here rather than trusting every build environment to be clean.
    if (import.meta.env.PROD && isPrivateUrl(url)) {
      return MOBILE_APP_PRODUCTION_URL;
    }

    return url;
  }

  // Unset in a production build: the real host, not a guess.
  if (import.meta.env.PROD) return MOBILE_APP_PRODUCTION_URL;

  if (typeof window === 'undefined') return '';

  // Dev only: derive a LAN-reachable URL from the current host, so a tester
  // browsing the portal over the LAN gets a URL their phone can resolve rather
  // than a `localhost` one it cannot.
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || /^192\.168\./.test(host);

  if (isLocal) {
    return `${window.location.protocol}//${host}:5173/voye`;
  }

  return '';
}

/**
 * The "Try a demo account" panel on the sign-in page.
 *
 * OFF unless explicitly enabled. That panel lists four addresses INCLUDING the
 * platform admin, with the shared password printed beside them — on a public
 * login page it is a map of how to get in, and it documents the password
 * convention even after the accounts themselves are disabled.
 *
 * Demo accounts only exist locally now (the production ones were purged or
 * suspended), so on production the panel advertises logins that cannot work.
 * Set VITE_DEMO_LOGINS=true for local dev and staging.
 */
export function demoLoginsEnabled(): boolean {
  return import.meta.env.VITE_DEMO_LOGINS === 'true';
}
